import React from 'react'
import {
	Box,
	Button,
	ButtonBase,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { mainGreen } from '../../../utils/color'
import { formatMoney, orderCode, parseMoney, paymentLabel, resolveImageUrl } from '../cashflowUtils'

export const emptyExpenseDetail = { open: false, title: '', photos: [], orders: [] }

const InfoLine = ({ label, value }) => {
	if (!value) return null
	return (
		<Stack direction='row' spacing={1} alignItems='flex-start'>
			<Typography sx={{ fontSize: 13, color: '#637381', flex: '0 0 96px' }}>{label}</Typography>
			<Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1, wordBreak: 'break-word' }}>{value}</Typography>
		</Stack>
	)
}

const SectionTitle = ({ children }) => (
	<Typography sx={{ fontWeight: 800, fontSize: 13, color: '#637381', textTransform: 'uppercase' }}>
		{children}
	</Typography>
)

const OrderCard = ({ order }) => {
	const items = (order.line_items || []).filter((item) => Number(item.quantity) > 0)
	const shippingTotal = parseMoney(order.shippingTotal)
	return (
		<Box
			sx={{
				p: 1.5,
				border: '1px solid #DDE7DF',
				borderRadius: '12px',
				background: '#F8FBF9',
			}}>
			<Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
				<Typography sx={{ fontWeight: 900 }}>{orderCode(order) || 'Đơn hàng'}</Typography>
				<Chip size='small' label={paymentLabel(order)} sx={{ height: 22 }} />
				{Number(order.quantity) > 0 && <Chip size='small' label={`${order.quantity}kg`} sx={{ height: 22 }} />}
				{order.quickPurchase && <Chip size='small' color='error' label='Mua nhanh' sx={{ height: 22 }} />}
				{order.edited?.value && <Chip size='small' label='Đã sửa' sx={{ height: 22 }} />}
				{order.tag?.map((tag) => (
					<Chip
						key={`${order._id}-${tag.name}`}
						size='small'
						label={tag.name}
						sx={{ height: 22, background: tag.color || '#ECEFF1', color: '#fff', fontWeight: 700 }}
					/>
				))}
			</Stack>
			{items.length > 0 && (
				<Stack spacing={0.25} sx={{ mt: 1 }}>
					{items.map((item, index) => (
						<Stack
							key={`${order._id}-item-${item.id ?? index}`}
							direction='row'
							spacing={1}
							alignItems='flex-start'
							justifyContent='space-between'>
							<Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1, wordBreak: 'break-word' }}>
								{item.quantity}x {item.name}
								{item.user_note ? ` (${item.user_note})` : ''}
							</Typography>
							<Typography sx={{ fontSize: 13, color: '#637381', fontVariantNumeric: 'tabular-nums' }}>
								{formatMoney(item.subtotal || item.price)}
							</Typography>
						</Stack>
					))}
				</Stack>
			)}
			<Stack spacing={0.25} sx={{ mt: 1 }}>
				<InfoLine label='Khách' value={order.customerName} />
				<InfoLine label='SĐT' value={order.customerPhone} />
				<InfoLine label='Địa chỉ' value={order.customerAddress} />
				<InfoLine label='Ghi chú' value={order.customerNote || order.detail} />
			</Stack>
			<Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap sx={{ mt: 1 }}>
				<Typography sx={{ fontSize: 13, fontWeight: 800 }}>Tổng đơn: {formatMoney(order.totalPrice)}</Typography>
				<Typography sx={{ fontSize: 13, fontWeight: 800, color: shippingTotal > 0 ? mainGreen : '#637381' }}>
					{shippingTotal > 0 ? `Phí ship: ${formatMoney(shippingTotal)}` : 'Không thu ship'}
				</Typography>
			</Stack>
		</Box>
	)
}

// Xem nhanh ảnh đối soát và đơn liên quan của khoản chi, mở ngay trên trang thu chi
// để trên điện thoại không phải kéo ngang bảng hay rời khỏi ngày đang xem
export default function ExpenseDetailDialog({ expenseDetail, setExpenseDetail, setImageViewer }) {
	const title = expenseDetail?.title || 'Chi tiết khoản chi'
	const photos = expenseDetail?.photos || []
	const orders = expenseDetail?.orders || []
	const closeDialog = () => setExpenseDetail(emptyExpenseDetail)
	return (
		<Dialog open={Boolean(expenseDetail?.open)} onClose={closeDialog} fullWidth maxWidth='sm'>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent dividers>
				{photos.length === 0 && orders.length === 0 ? (
					<Typography sx={{ color: '#637381' }}>Khoản chi này chưa có ảnh hay đơn liên quan</Typography>
				) : (
					<Stack spacing={2}>
						{photos.length > 0 && (
							<Stack spacing={1}>
								<SectionTitle>{`Ảnh đính kèm (${photos.length})`}</SectionTitle>
								<Box
									sx={{
										display: 'grid',
										gap: 1,
										gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
									}}>
									{photos.map((photo, photoIndex) => {
										const photoUrl = resolveImageUrl(photo)
										const photoTitle = `${title} - ảnh ${photoIndex + 1}`
										return (
											<ButtonBase
												key={`detail-photo-${photoIndex}`}
												aria-label={photoTitle}
												onClick={() => setImageViewer?.({ open: true, url: photoUrl, title: photoTitle })}
												sx={{
													width: '100%',
													aspectRatio: '1 / 1',
													borderRadius: '10px',
													overflow: 'hidden',
													border: '1px solid #DDE7DF',
													background: '#F8FBF9',
												}}>
												<Box
													component='img'
													loading='lazy'
													src={photoUrl}
													alt={photoTitle}
													sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
												/>
											</ButtonBase>
										)
									})}
								</Box>
							</Stack>
						)}
						{orders.length > 0 && (
							<Stack spacing={1}>
								<SectionTitle>{`Đơn liên quan (${orders.length})`}</SectionTitle>
								{orders.map((order, index) => (
									<OrderCard key={order._id || index} order={order} />
								))}
							</Stack>
						)}
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Button startIcon={<CloseIcon />} onClick={closeDialog}>
					Đóng
				</Button>
			</DialogActions>
		</Dialog>
	)
}
